import type { LngLatBounds, Map as MapSDK, LngLat } from "@maptiler/sdk";
import type { TileLayer } from "../layers/TileLayer";
import { LRUCache } from "lru-cache";

/**
 * A number in [0, 255]
 */
export type ColorChannelValue = number; //0-255

/**
 * A number in [0, 1]
 */
export type AlphaChannelValue = number; // 0 - 1

/**
 * RgbChannel
 */
export type RgbChannel = "r" | "b" | "g";

/**
 * Channel(s) in a use by a layer
 */
export type UsedRgbChannel = RgbChannel | `${RgbChannel}${RgbChannel}`;

/**
 * Indices of RGBA image channels
 */
export enum ChannelIndices {
  r = 0,
  g = 1,
  b = 2,
  a = 3,
}

// Cache for GL textures to be used on picking
let textureCache: LRUCache<HTMLImageElement, WebGLTexture> | null = null;
let canvasForGlPicking: HTMLCanvasElement | null = null;
let glContextForPicking: WebGL2RenderingContext | null = null;

function getGlContextForPicking(): WebGL2RenderingContext {
  if (!canvasForGlPicking || !glContextForPicking) {
    canvasForGlPicking = document.createElement("canvas");
    glContextForPicking = canvasForGlPicking.getContext("webgl2");
    const maxNbTextures = glContextForPicking?.getParameter(glContextForPicking.MAX_TEXTURE_IMAGE_UNITS);

    textureCache = new LRUCache<HTMLImageElement, WebGLTexture>({
      max: maxNbTextures,

      // When a new texture added while the cache is full, we clear the least recently used texture from GPU memory
      dispose: (value, _key) => {
        const gl = getGlContextForPicking();
        gl.deleteTexture(value);
      },
    });
  }

  return <WebGL2RenderingContext>glContextForPicking;
}

/**
 * Defines a color type made of 3 ColorChannelValue OR 3 ColorChannelValue and one AlphaChannelValue
 */
export type RgbaColor =
  | [ColorChannelValue, ColorChannelValue, ColorChannelValue]
  | [ColorChannelValue, ColorChannelValue, ColorChannelValue, AlphaChannelValue];

/**
 * Turns a RgbaColor into a string GLSL color constructor.
 * @param color
 * @returns a GLSL vec4 color constructor
 */
export function floatifyColor(color: RgbaColor): string {
  return (
    `vec4(${(color[0] / 255).toFixed(3)},` +
    `${(color[1] / 255).toFixed(3)},` +
    `${(color[2] / 255).toFixed(3)},` +
    `${((color[3] ?? 255) / 255).toFixed(3)})`
  );
}

/**
 * Turns a [r, g, b] or [r, g, b, a] color defined on the [0, 255] range
 * into a [r, g, b, a] color in the [0, 1] range
 * @param color
 * @returns
 */
export function makeUnitColor(color: RgbaColor): RgbaColor {
  return [color[0] / 255, color[1] / 255, color[2] / 255, (color[3] ?? 255) / 255];
}

/**
 * Converts a latitude in degree into a unit value.
 * See the function here: https://www.desmos.com/calculator/ptaooqjt8c
 * @param lat
 * @returns
 */
export function latToUnit(lat: number) {
  return ((180 / Math.PI) * Math.log(Math.tan(Math.PI / 4 + (lat * Math.PI) / 360))) / 360;
}

/**
 * Converts WGS84 coordinates to [-0.5, 0.5] range.
 * @param lng
 * @param lat
 * @internal
 */
export function wgs84ToUnit(lng: number, lat: number) {
  return [lng / 360, latToUnit(lat)] as const;
}

/**
 * Converts unit range [-0.5, 0.5] to WGS84 coordinates.
 * @param unit
 * @returns
 */
export function unitToLat(unit: number) {
  const pi = Math.PI;
  const atan = Math.atan;
  const exp = Math.exp;

  const thing1 = exp(unit * 2 * pi);
  const thing2 = atan(thing1) - pi / 4;
  return (thing2 * 360) / pi;
}

/**
 * With a mercator image (origin: top left), return the latitude from the y in pixel coordinates
 * @param y
 * @param imageSize
 * @returns
 */
export function pixelToLat(y: number, imageSize: number): number {
  const unit = -0.5 * ((y / imageSize) * 2 - 1);
  return unitToLat(unit);
}

/**
 * Radius of the Earth in meter
 */
export const EARTH_RADIUS_M = 6378137;

/**
 * Perimeter of the Earth alongside the equator in meter
 */
export const EARTH_PERIMETER_M = 2 * Math.PI * EARTH_RADIUS_M;

/**
 * Converts wgs84 (longitude and latitude) into Mercator coordinates (in meters)
 * @param lng
 * @param lat
 * @returns
 */
export function wgs84ToMerc(lng: number, lat: number) {
  return wgs84ToUnit(lng, lat).map((a) => a * EARTH_PERIMETER_M);
}

// export function metersToLatLon(mx: number, my: number) {
//   // Converts XY point from Spherical Mercator EPSG:900913 to lat/lon in WGS84 Datum

//   const lon = (mx / EARTH_PERIMETER_M / 2) * 180.0
//   let lat = (my / EARTH_PERIMETER_M / 2) * 180.0

//   lat = 180 / Math.PI * (2 * Math.atan( Math.exp( lat * Math.PI / 180.0)) - Math.PI / 2.0)
//   return { lat, lon }
// }

/**
 * A Pixel as in HTMLImageElement, with the form `{r, g, b, a}`
 */
export interface Pixel extends Record<RgbChannel, ColorChannelValue> {
  a: AlphaChannelValue;
}

/**
 * Get the pixel value of an image at a given position
 * @param image
 * @param x
 * @param y
 * @returns The pixel as `{r, g, b, a}` in a [0, 1] interval (and not in [0, 255])
 */
export function sampleImage(image: HTMLImageElement, x: number, y: number): Pixel {
  // For future maintainer:
  // picking a pixel value using a WebGL2 context instead of a simple canvas
  // may seem overkill, yet, canvas are applying alpha premultiplication, meaning they mess up RGB
  // values if the alpha value is not 255. This is usually not a big deal when the image contains colors
  // but this could lead to significant issues if the image contains non-color data, such as terrain RGB
  // or radar data making use of the alpha channel to use as a category.
  // More info about this issue here:
  // https://stackoverflow.com/questions/23497925/how-can-i-stop-the-alpha-premultiplication-with-canvas-imagedata
  const xImage = Math.floor(x * image.width);
  const yImage = Math.floor(y * image.height);
  const gl = getGlContextForPicking();

  if (gl == null) {
    throw new Error("Failed to obtain WebGL2 context");
  }

  if (!textureCache) {
    throw new Error("Could not initialize texture cache");
  }

  gl.activeTexture(gl.TEXTURE0);
  let texture: WebGLTexture;

  if (textureCache.has(image)) {
    texture = textureCache.get(image) as WebGLTexture;
  } else {
    texture = gl.createTexture() as WebGLTexture;
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, image);
    textureCache.set(image, texture);
  }

  const framebuffer = gl.createFramebuffer();
  gl.bindFramebuffer(gl.FRAMEBUFFER, framebuffer);
  gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, texture, 0);

  gl.drawBuffers([gl.COLOR_ATTACHMENT0]);

  const data = new Uint8Array(4);
  gl.readPixels(xImage, yImage, 1, 1, gl.RGBA, gl.UNSIGNED_BYTE, data);
  gl.deleteFramebuffer(framebuffer);

  return {
    r: data[0] / 255,
    g: data[1] / 255,
    b: data[2] / 255,
    a: data[3] / 255,
  };
}

export function addLayerWhenReady(map: MapSDK, layer: TileLayer, beforeId = "") {
  if (map.isStyleLoaded()) {
    map.addLayer(layer, beforeId);
  } else {
    map.once("load", () => {
      map.addLayer(layer, beforeId);
    });
  }
}

/**
 * Get the cardinal direction based on an angle with
 * - `0°`: true north
 * - `90°`: east
 * - `180°`: south
 * - `270°`: west
 * @param angle
 * @returns
 */
export function getCardinalDirection(angle: number) {
  // Define array of direction names
  const directions = [
    "N",
    "NNE",
    "NE",
    "ENE",
    "E",
    "ESE",
    "SE",
    "SSE",
    "S",
    "SSW",
    "SW",
    "WSW",
    "W",
    "WNW",
    "NW",
    "NNW",
  ];

  // Calculate index of closest direction based on angle
  const index = Math.round(((angle + 360) % 360) / 22.5) % 16;

  // Return corresponding direction name
  return directions[index];
}

export function wgs84ToMaplibreMerc(lng: number, lat: number) {
  const merc = wgs84ToUnit(lng, lat);
  return [merc[0] + 0.5, merc[1] * -1 + 0.5];
}

export function wgs84ToTileIndex(lngLat: LngLat, z: number) {
  const mercCoord = wgs84ToMaplibreMerc(lngLat.lng, lngLat.lat);
  const nbTile = 2 ** z;
  return {
    z: z,
    x: mercCoord[0] * nbTile,
    y: mercCoord[1] * nbTile,
  };
}

function getTileCoverage(wgs84Bound: LngLatBounds, z: number): string[] {
  const ceiledZ = ~~z;
  const topLeft = wgs84ToTileIndex(wgs84Bound.getNorthWest(), ceiledZ);
  const bottomRight = wgs84ToTileIndex(wgs84Bound.getSouthEast(), ceiledZ);
  const tiles = [];

  for (let x = Math.floor(topLeft.x); x <= bottomRight.x; x += 1) {
    for (let y = Math.floor(topLeft.y); y <= Math.floor(bottomRight.y); y += 1) {
      tiles.push(`${z}/${x}/${y}`);
    }
  }

  return tiles;
}

export { getTileCoverage };
